import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, Database, Server, Cloud, Shield, Activity, X, DollarSign, Clock, Cpu, HardDrive, Network, Star, Gem, Crown, Sparkles, Lock } from 'lucide-react';
import { componentsAPI } from '../services/api';
import { toast } from 'react-toastify';
import useUserStore from '../store/userStore';

const CATEGORY_ICONS = {
  compute: Server,
  storage: Database,
  cache: Zap,
  queue: Activity,
  loadbalancer: Cloud,
  database: Database,
  networking: Cloud,
  monitoring: Activity,
  security: Shield,
};

const CATEGORY_COLORS = {
  compute: 'bg-blue-500',
  storage: 'bg-green-500',
  cache: 'bg-yellow-500',
  queue: 'bg-purple-500',
  loadbalancer: 'bg-indigo-500',
  database: 'bg-emerald-500',
  networking: 'bg-cyan-500',
  monitoring: 'bg-orange-500',
  security: 'bg-red-500',
};

const PROVIDER_CONFIG = {
  aws: { name: 'AWS', color: 'bg-orange-500', textColor: 'text-orange-700', bgLight: 'bg-orange-100' },
  azure: { name: 'Azure', color: 'bg-blue-600', textColor: 'text-blue-700', bgLight: 'bg-blue-100' },
  gcp: { name: 'GCP', color: 'bg-red-500', textColor: 'text-red-700', bgLight: 'bg-red-100' },
  open_source: { name: 'Open Source', color: 'bg-green-600', textColor: 'text-green-700', bgLight: 'bg-green-100' },
  generic: { name: 'Generic', color: 'bg-gray-500', textColor: 'text-gray-700', bgLight: 'bg-gray-100' },
};

const RARITY_CONFIG = {
  common: {
    name: 'Common',
    color: 'bg-gray-100 text-gray-700 border-gray-300',
    cardBorder: 'border-gray-200',
    glow: '',
    icon: null,
  },
  uncommon: {
    name: 'Uncommon',
    color: 'bg-green-100 text-green-700 border-green-300',
    cardBorder: 'border-green-300',
    glow: '',
    icon: null,
  },
  rare: {
    name: 'Rare',
    color: 'bg-blue-100 text-blue-700 border-blue-400',
    cardBorder: 'border-blue-400',
    glow: 'shadow-blue-200',
    icon: Star,
  },
  epic: {
    name: 'Epic',
    color: 'bg-purple-100 text-purple-700 border-purple-400',
    cardBorder: 'border-purple-400',
    glow: 'shadow-purple-300 shadow-lg',
    icon: Gem,
  },
  legendary: {
    name: 'Legendary',
    color: 'bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-700 border-amber-400',
    cardBorder: 'border-amber-400 border-2',
    glow: 'shadow-amber-300 shadow-xl',
    icon: Crown,
  },
};

const TIER_CONFIG = {
  basic: { name: 'Basic', color: 'bg-gray-50 text-gray-600', icon: '1' },
  standard: { name: 'Standard', color: 'bg-blue-50 text-blue-600', icon: '2' },
  pro: { name: 'Pro', color: 'bg-purple-50 text-purple-600', icon: '3' },
  enterprise: { name: 'Enterprise', color: 'bg-amber-50 text-amber-600', icon: '4' },
};

const RarityBadge = ({ rarity }) => {
  const config = RARITY_CONFIG[rarity] || RARITY_CONFIG.common;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${config.color}`}>
      {Icon && <Icon className="w-3 h-3" />}
      {config.name}
    </span>
  );
};

const TierBadge = ({ tier }) => {
  const config = TIER_CONFIG[tier] || TIER_CONFIG.basic;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.color}`}>
      T{config.icon}
    </span>
  );
};

const ComponentDetailModal = ({ component, onClose }) => {
  const Icon = CATEGORY_ICONS[component.category] || Server;
  const colorClass = CATEGORY_COLORS[component.category] || 'bg-gray-500';
  const provider = component.cost_profile?.provider || component.provider || 'generic';
  const providerConfig = PROVIDER_CONFIG[provider] || PROVIDER_CONFIG.generic;
  const costProfile = component.cost_profile || {};
  const rarity = component.rarity || 'common';
  const tier = component.tier || 'basic';
  const rarityConfig = RARITY_CONFIG[rarity] || RARITY_CONFIG.common;
  const tierConfig = TIER_CONFIG[tier] || TIER_CONFIG.basic;
  const stats = component.stats || {};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div
        className={`bg-slate-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-slate-700 ${rarityConfig.glow}`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`${colorClass} p-6 text-white relative`}>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
              <Icon className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-1">{component.name}</h2>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${providerConfig.bgLight} ${providerConfig.textColor}`}>
                  {providerConfig.name}
                </span>
                <span className="px-2 py-0.5 rounded text-xs font-medium bg-white/20 capitalize">
                  {component.category}
                </span>
                <RarityBadge rarity={rarity} />
                <TierBadge tier={tier} />
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Description */}
          <div>
            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-2">Description</h3>
            <p className="text-slate-300 leading-relaxed">{component.description}</p>
          </div>

          {/* Cost Profile */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Performance & Cost</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {costProfile.monthly_cost !== undefined && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                    <DollarSign className="w-3 h-3" />
                    Monthly Cost
                  </div>
                  <div className="text-lg font-bold text-gray-900">${costProfile.monthly_cost}</div>
                </div>
              )}
              {costProfile.qps_capacity !== undefined && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                    <Network className="w-3 h-3" />
                    QPS Capacity
                  </div>
                  <div className="text-lg font-bold text-gray-900">{costProfile.qps_capacity.toLocaleString()}</div>
                </div>
              )}
              {costProfile.latency_ms !== undefined && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                    <Clock className="w-3 h-3" />
                    Latency
                  </div>
                  <div className="text-lg font-bold text-gray-900">{costProfile.latency_ms}ms</div>
                </div>
              )}
              {costProfile.storage_gb !== undefined && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                    <HardDrive className="w-3 h-3" />
                    Storage
                  </div>
                  <div className="text-lg font-bold text-gray-900">{costProfile.storage_gb.toLocaleString()} GB</div>
                </div>
              )}
              {costProfile.memory_gb !== undefined && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                    <Cpu className="w-3 h-3" />
                    Memory
                  </div>
                  <div className="text-lg font-bold text-gray-900">{costProfile.memory_gb} GB</div>
                </div>
              )}
              {costProfile.throughput_msg_sec !== undefined && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                    <Activity className="w-3 h-3" />
                    Throughput
                  </div>
                  <div className="text-lg font-bold text-gray-900">{costProfile.throughput_msg_sec.toLocaleString()} msg/s</div>
                </div>
              )}
            </div>
          </div>

          {/* Detailed Stats */}
          {stats && Object.keys(stats).length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Detailed Stats</h3>
              <div className="space-y-4">
                {/* Performance Stats */}
                {stats.performance && Object.keys(stats.performance).length > 0 && (
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-blue-700 mb-2">Performance</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {stats.performance.qps_read && (
                        <div><span className="text-xs text-gray-500">Read QPS:</span> <span className="font-medium">{stats.performance.qps_read.toLocaleString()}</span></div>
                      )}
                      {stats.performance.qps_write && (
                        <div><span className="text-xs text-gray-500">Write QPS:</span> <span className="font-medium">{stats.performance.qps_write.toLocaleString()}</span></div>
                      )}
                      {stats.performance.latency_p50_ms && (
                        <div><span className="text-xs text-gray-500">P50 Latency:</span> <span className="font-medium">{stats.performance.latency_p50_ms}ms</span></div>
                      )}
                      {stats.performance.latency_p99_ms && (
                        <div><span className="text-xs text-gray-500">P99 Latency:</span> <span className="font-medium">{stats.performance.latency_p99_ms}ms</span></div>
                      )}
                      {stats.performance.throughput_mbps && (
                        <div><span className="text-xs text-gray-500">Throughput:</span> <span className="font-medium">{stats.performance.throughput_mbps.toLocaleString()} MB/s</span></div>
                      )}
                    </div>
                  </div>
                )}

                {/* Reliability Stats */}
                {stats.reliability && Object.keys(stats.reliability).length > 0 && (
                  <div className="bg-green-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-green-700 mb-2">Reliability</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {stats.reliability.sla_percentage && (
                        <div><span className="text-xs text-gray-500">SLA:</span> <span className="font-medium">{stats.reliability.sla_percentage}%</span></div>
                      )}
                      {stats.reliability.availability_zones && (
                        <div><span className="text-xs text-gray-500">AZs:</span> <span className="font-medium">{stats.reliability.availability_zones}</span></div>
                      )}
                      {stats.reliability.durability_nines && (
                        <div><span className="text-xs text-gray-500">Durability:</span> <span className="font-medium">{stats.reliability.durability_nines} 9s</span></div>
                      )}
                      {stats.reliability.auto_failover && (
                        <div><span className="text-xs text-gray-500">Failover:</span> <span className="font-medium text-green-600">Auto</span></div>
                      )}
                    </div>
                  </div>
                )}

                {/* Scalability Stats */}
                {stats.scalability && Object.keys(stats.scalability).length > 0 && (
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-purple-700 mb-2">Scalability</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {stats.scalability.horizontal_scaling && (
                        <div><span className="text-xs text-gray-500">Horizontal:</span> <span className="font-medium text-green-600">Yes</span></div>
                      )}
                      {stats.scalability.auto_scaling && (
                        <div><span className="text-xs text-gray-500">Auto-scale:</span> <span className="font-medium text-green-600">Yes</span></div>
                      )}
                      {stats.scalability.max_nodes && (
                        <div><span className="text-xs text-gray-500">Max Nodes:</span> <span className="font-medium">{stats.scalability.max_nodes.toLocaleString()}</span></div>
                      )}
                      {stats.scalability.max_instances && (
                        <div><span className="text-xs text-gray-500">Max Instances:</span> <span className="font-medium">{stats.scalability.max_instances.toLocaleString()}</span></div>
                      )}
                    </div>
                  </div>
                )}

                {/* Security Stats */}
                {stats.security && Object.keys(stats.security).length > 0 && (
                  <div className="bg-red-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-red-700 mb-2">Security</h4>
                    <div className="flex flex-wrap gap-2">
                      {stats.security.encryption_at_rest && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Encryption at Rest</span>
                      )}
                      {stats.security.encryption_in_transit && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Encryption in Transit</span>
                      )}
                      {stats.security.compliance && stats.security.compliance.map(c => (
                        <span key={c} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">{c}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Features */}
          {Object.keys(costProfile).filter(k =>
            !['monthly_cost', 'qps_capacity', 'latency_ms', 'storage_gb', 'memory_gb', 'throughput_msg_sec', 'throughput_mb_sec', 'provider', 'cpu_cores'].includes(k) &&
            typeof costProfile[k] !== 'number'
          ).length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Features</h3>
              <div className="flex flex-wrap gap-2">
                {Object.entries(costProfile).map(([key, value]) => {
                  if (['monthly_cost', 'qps_capacity', 'latency_ms', 'storage_gb', 'memory_gb', 'throughput_msg_sec', 'throughput_mb_sec', 'provider', 'cpu_cores', 'iops'].includes(key)) return null;
                  if (typeof value === 'boolean' && value) {
                    return (
                      <span key={key} className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                        {key.replace(/_/g, ' ')}
                      </span>
                    );
                  }
                  if (Array.isArray(value)) {
                    return (
                      <span key={key} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                        {key.replace(/_/g, ' ')}: {value.join(', ')}
                      </span>
                    );
                  }
                  if (typeof value === 'string' && value !== 'generic') {
                    return (
                      <span key={key} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                        {key.replace(/_/g, ' ')}: {value}
                      </span>
                    );
                  }
                  return null;
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ComponentCard = ({ component, onClick, isPremiumLocked }) => {
  const Icon = CATEGORY_ICONS[component.category] || Server;
  const colorClass = CATEGORY_COLORS[component.category] || 'bg-gray-500';
  const provider = component.cost_profile?.provider || component.provider || 'generic';
  const providerConfig = PROVIDER_CONFIG[provider] || PROVIDER_CONFIG.generic;
  const rarity = component.rarity || 'common';
  const tier = component.tier || 'basic';
  const rarityConfig = RARITY_CONFIG[rarity] || RARITY_CONFIG.common;

  return (
    <div
      onClick={onClick}
      className={`relative border rounded-lg p-4 transition-all cursor-pointer bg-slate-800/50 hover:shadow-lg hover:scale-[1.02] ${rarityConfig.cardBorder} ${rarityConfig.glow} ${isPremiumLocked ? 'opacity-75' : ''}`}
    >
      {/* Premium Lock Badge */}
      {isPremiumLocked && (
        <div className="absolute -top-2 -right-2 z-10">
          <div className="w-7 h-7 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center shadow-lg">
            <Lock className="w-3.5 h-3.5 text-white" />
          </div>
        </div>
      )}

      {/* Top row: Provider + Tier */}
      <div className="flex items-center justify-between mb-2">
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${providerConfig.bgLight} ${providerConfig.textColor}`}>
          {providerConfig.name}
        </span>
        <TierBadge tier={tier} />
      </div>

      {/* Category Icon */}
      <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg ${colorClass} text-white mb-3 ${isPremiumLocked ? 'grayscale' : ''}`}>
        <Icon className="w-6 h-6" />
      </div>

      {/* Component Info */}
      <h3 className={`font-semibold text-lg mb-1 ${isPremiumLocked ? 'text-slate-400' : 'text-white'}`}>{component.name}</h3>
      <p className="text-sm text-slate-400 mb-3 line-clamp-2">{component.description}</p>

      {/* Cost Info */}
      <div className="text-xs text-slate-500 mb-3">
        <div>Monthly: ${component.cost_profile?.monthly_cost || 0}</div>
        {component.stats?.performance?.qps_read && (
          <div>QPS: {component.stats.performance.qps_read.toLocaleString()}</div>
        )}
      </div>

      {/* Bottom row: Rarity + Category */}
      <div className="flex items-center gap-2 flex-wrap">
        <RarityBadge rarity={rarity} />
        <span className="inline-block px-2 py-1 text-xs rounded bg-slate-700 text-slate-300 capitalize">
          {component.category}
        </span>
        {isPremiumLocked && (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-400 border border-amber-500/30">
            <Crown className="w-3 h-3" />
            Premium
          </span>
        )}
      </div>
    </div>
  );
};

const UpgradePromptModal = ({ onClose }) => {
  const navigate = useNavigate();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div
        className="bg-slate-800 rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-700"
        onClick={e => e.stopPropagation()}
      >
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center">
            <Crown className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">Premium Component</h3>
          <p className="text-slate-400 mb-6">
            This cloud provider component requires a Premium subscription. Upgrade to access all AWS, Azure, and GCP components.
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-600 rounded-lg text-slate-300 hover:bg-slate-700 transition-colors"
            >
              Maybe Later
            </button>
            <button
              onClick={() => navigate('/pricing')}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-lg hover:from-amber-600 hover:to-orange-600 transition-colors"
            >
              Upgrade Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ComponentInventory = ({ filterCategory = null }) => {
  const navigate = useNavigate();
  const { user } = useUserStore();
  const [allComponents, setAllComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(filterCategory);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedRarity, setSelectedRarity] = useState(null);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [accessFilter, setAccessFilter] = useState('all'); // 'all', 'free', 'premium'

  useEffect(() => {
    loadComponents();
  }, []);

  const loadComponents = async () => {
    try {
      setLoading(true);
      const response = await componentsAPI.getAll();
      let componentsList = [];
      if (response.data?.components && Array.isArray(response.data.components)) {
        componentsList = response.data.components;
      } else if (response.data?.by_category && typeof response.data.by_category === 'object') {
        componentsList = Object.values(response.data.by_category).flat();
      }
      setAllComponents(componentsList);
    } catch (error) {
      console.error('Error loading components:', error);
      toast.error('Failed to load components');
    } finally {
      setLoading(false);
    }
  };

  const categories = [...new Set(allComponents.map((c) => c.category))].sort();
  const providers = [...new Set(allComponents.map((c) => c.cost_profile?.provider || c.provider || 'generic'))].sort();
  const rarities = ['common', 'uncommon', 'rare', 'epic', 'legendary'];

  // Check if component is premium locked for user
  const isComponentLocked = (component) => {
    // If user is premium, nothing is locked
    if (user?.is_premium) return false;
    // Use backend flags if available
    if (component.can_access !== undefined) return !component.can_access;
    if (component.is_premium_required !== undefined) return component.is_premium_required;
    // Fallback: non-starter components are premium
    return !component.is_starter;
  };

  const filteredComponents = allComponents.filter((c) => {
    if (selectedCategory && c.category !== selectedCategory) return false;
    if (selectedProvider && (c.cost_profile?.provider || c.provider || 'generic') !== selectedProvider) return false;
    if (selectedRarity && (c.rarity || 'common') !== selectedRarity) return false;
    if (accessFilter === 'free' && isComponentLocked(c)) return false;
    if (accessFilter === 'premium' && !isComponentLocked(c)) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return c.name.toLowerCase().includes(query) || c.description?.toLowerCase().includes(query);
    }
    return true;
  });

  const handleComponentClick = (component) => {
    const isLocked = isComponentLocked(component);
    if (isLocked) {
      setShowUpgradeModal(true);
    } else {
      setSelectedComponent(component);
    }
  };

  const freeCount = allComponents.filter(c => !isComponentLocked(c)).length;
  const premiumCount = allComponents.filter(c => isComponentLocked(c)).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-gray-500">Loading components...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-white">Component Library</h2>
        <p className="text-slate-400">
          {user?.is_premium ? (
            <>All {allComponents.length} components available for your system designs</>
          ) : (
            <>{freeCount} free components available • <span className="text-amber-400">{premiumCount} premium components</span> require upgrade</>
          )}
        </p>
      </div>

      {/* Access Filter (only show for non-premium users) */}
      {!user?.is_premium && (
        <div>
          <div className="text-sm font-medium text-slate-400 mb-2">Access Level</div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setAccessFilter('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                accessFilter === 'all' ? 'bg-slate-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              All ({allComponents.length})
            </button>
            <button
              onClick={() => setAccessFilter('free')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                accessFilter === 'free' ? 'bg-green-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              Free ({freeCount})
            </button>
            <button
              onClick={() => setAccessFilter('premium')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                accessFilter === 'premium' ? 'bg-amber-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              <Lock className="w-3 h-3" />
              Premium ({premiumCount})
            </button>
          </div>
        </div>
      )}

      {/* Search */}
      <div>
        <input
          type="text"
          placeholder="Search components..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-4 py-2 border border-slate-600 bg-slate-700 text-white placeholder-slate-400 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>

      {/* Provider Filter */}
      <div>
        <div className="text-sm font-medium text-slate-400 mb-2">Cloud Provider</div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setSelectedProvider(null)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              !selectedProvider ? 'bg-slate-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            All Providers
          </button>
          {providers.map((provider) => {
            const config = PROVIDER_CONFIG[provider] || PROVIDER_CONFIG.generic;
            return (
              <button
                key={provider}
                onClick={() => setSelectedProvider(provider)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedProvider === provider
                    ? `${config.color} text-white`
                    : `${config.bgLight} ${config.textColor} hover:opacity-80`
                }`}
              >
                {config.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Rarity Filter */}
      <div>
        <div className="text-sm font-medium text-slate-400 mb-2">Rarity</div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setSelectedRarity(null)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              !selectedRarity ? 'bg-slate-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            All Rarities
          </button>
          {rarities.map((rarity) => {
            const config = RARITY_CONFIG[rarity];
            return (
              <button
                key={rarity}
                onClick={() => setSelectedRarity(rarity)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
                  selectedRarity === rarity
                    ? config.color
                    : 'bg-slate-700 text-slate-300 border-slate-600 hover:bg-slate-600'
                }`}
              >
                {config.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Category Filter */}
      <div>
        <div className="text-sm font-medium text-slate-400 mb-2">Category</div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              !selectedCategory ? 'bg-primary-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            All Categories
          </button>
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize ${
                selectedCategory === category ? 'bg-primary-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Results count */}
      <div className="text-sm text-slate-400">
        Showing {filteredComponents.length} of {allComponents.length} components
      </div>

      {/* Components Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredComponents.map((component) => (
          <ComponentCard
            key={component.id}
            component={component}
            onClick={() => handleComponentClick(component)}
            isPremiumLocked={isComponentLocked(component)}
          />
        ))}
      </div>

      {filteredComponents.length === 0 && (
        <div className="text-center py-12 text-slate-400">
          No components found with the selected filters
        </div>
      )}

      {/* Component Detail Modal */}
      {selectedComponent && (
        <ComponentDetailModal
          component={selectedComponent}
          onClose={() => setSelectedComponent(null)}
        />
      )}

      {/* Premium Upgrade Modal */}
      {showUpgradeModal && (
        <UpgradePromptModal onClose={() => setShowUpgradeModal(false)} />
      )}
    </div>
  );
};

export default ComponentInventory;
